import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTournament } from '../../services/api';
import { GenderMultiSelect, USTALevelField } from '../forms';
import { Gender } from '../../constants/userConstants';

interface CreateTournamentDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateTournamentDialog = ({ open, onClose }: CreateTournamentDialogProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'ladder' as 'ladder' | 'round_robin_ladder',
    location: '',
    start_date: '',
    end_date: '',
    registration_start_date: '',
    registration_end_date: '',
    allowed_genders: [] as Gender[],
    min_level: '',
    max_level: '',
    third_set_format: 'full_set' as 'full_set' | 'match_tiebreak',
    min_players: '',
    num_pools: '',
    match_type_config: 'singles' as 'singles' | 'doubles' | 'mixed_doubles',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: createTournament,
    onSuccess: () => {
      queryClient.invalidateQueries(['tournaments']);
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create tournament');
    },
  });

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: 'ladder',
      location: '',
      start_date: '',
      end_date: '',
      registration_start_date: '',
      registration_end_date: '',
      allowed_genders: [],
      min_level: '',
      max_level: '',
      third_set_format: 'full_set',
      min_players: '',
      num_pools: '',
      match_type_config: 'singles',
    });
    setError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate dates
    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      setError('End date must be after start date');
      return;
    }

    // Validate registration dates if provided
    if (formData.registration_start_date && formData.registration_end_date) {
      if (new Date(formData.registration_start_date) >= new Date(formData.registration_end_date)) {
        setError('Registration end date must be after registration start date');
        return;
      }
    }

    // Validate USTA levels
    if (formData.min_level && formData.max_level) {
      if (parseFloat(formData.min_level) > parseFloat(formData.max_level)) {
        setError('Minimum USTA level must be less than or equal to maximum level');
        return;
      }
    }

    // No doubles configuration validation needed - match type determines everything

    // Prepare data for submission
    const submitData: any = {
      ...formData,
      allowed_genders: formData.allowed_genders.length > 0 ? formData.allowed_genders : undefined,
      min_level: formData.min_level || undefined,
      max_level: formData.max_level || undefined,
      registration_start_date: formData.registration_start_date || undefined,
      registration_end_date: formData.registration_end_date || undefined,
      min_players: formData.min_players ? parseInt(formData.min_players) : undefined,
      num_pools: formData.num_pools ? parseInt(formData.num_pools) : undefined,
    };

    // For mixed doubles, clear gender restrictions (always mixed)
    if (formData.match_type_config === 'mixed_doubles') {
      submitData.allowed_genders = undefined;
    }

    createMutation.mutate(submitData);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create New Tournament</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tournament Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tournament Type"
                value={formData.type}
                onChange={handleChange('type')}
                required
              >
                <MenuItem value="ladder">Ladder (Single Elimination)</MenuItem>
                <MenuItem value="round_robin_ladder">Round Robin + Ladder</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Match Type"
                value={formData.match_type_config}
                onChange={handleChange('match_type_config')}
                required
                helperText="Choose the type of matches for this tournament"
              >
                <MenuItem value="singles">Singles</MenuItem>
                <MenuItem value="doubles">Doubles</MenuItem>
                <MenuItem value="mixed_doubles">Mixed Doubles</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Third Set Format"
                value={formData.third_set_format}
                onChange={handleChange('third_set_format')}
                helperText="Choose how the third set is played"
              >
                <MenuItem value="full_set">Full Set (First to 6 games, win by 2)</MenuItem>
                <MenuItem value="match_tiebreak">Match Tiebreak (First to 10 points, win by 2)</MenuItem>
              </TextField>
            </Grid>

            {/* Gender selection removed for mixed doubles - always mixed gender */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleChange('location')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.start_date || new Date().toISOString().split('T')[0] }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Registration Period (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Start Date"
                type="date"
                value={formData.registration_start_date}
                onChange={handleChange('registration_start_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                helperText="Leave empty for open registration"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration End Date"
                type="date"
                value={formData.registration_end_date}
                onChange={handleChange('registration_end_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.registration_start_date || new Date().toISOString().split('T')[0] }}
                helperText="Leave empty for open registration"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Eligibility Requirements (Optional)
              </Typography>
            </Grid>

            {/* Hide gender selection for mixed doubles - always mixed gender */}
            {formData.match_type_config !== 'mixed_doubles' && (
              <Grid item xs={12}>
                <GenderMultiSelect
                  value={formData.allowed_genders}
                  onChange={(value) => setFormData({ ...formData, allowed_genders: value })}
                  helperText="Leave unchecked to allow all genders"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.min_level}
                onChange={(value) => setFormData({ ...formData, min_level: value })}
                label="Minimum USTA Level"
                helperText="Leave empty for no minimum"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.max_level}
                onChange={(value) => setFormData({ ...formData, max_level: value })}
                label="Maximum USTA Level"
                helperText="Leave empty for no maximum"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Players"
                value={formData.min_players}
                onChange={handleChange('min_players')}
                inputProps={{ min: 2 }}
                helperText="Minimum number of players required to start"
              />
            </Grid>
            
            {formData.type === 'round_robin_ladder' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Pools"
                  value={formData.num_pools}
                  onChange={handleChange('num_pools')}
                  inputProps={{ min: 2 }}
                  helperText="Leave empty to auto-calculate. Minimum 3 players per pool required."
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Tournament'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateTournamentDialog;
